// The browser's side of `GET /runs/{run_id}/events/stream` (#158 serves it,
// #159 reads it).
//
// One `EventSource` per run page. Every frame's `data` is a `LogRow` — the
// same shape `read_log` returns, whether the frame is the replay of the log
// or the live tail — and every one goes to the same `applyEvent`. That is the
// whole of "one code path for live and replay": the client does not know
// which half of the stream it is in, and nothing here changes shape at the
// terminal status, because the stream does not end there (#6).
//
// **Resume is `Last-Event-ID`, and the browser sends it.** The server puts
// `id: <seq>` on every durable row and on every heartbeat; the `EventSource`
// remembers the last `id` it saw and, when the connection drops, reconnects
// with it as `Last-Event-ID` (the EventSource spec, §9.2.6 "reestablish the
// connection"). The server answers with the rows past that seq and a
// heartbeat at the new cursor. So a mid-session reconnect asks for exactly
// what the tab has not folded, and a reload — no last id — asks for the whole
// log, which is the replay. The client's part is to keep the cursor honest
// and the fold idempotent: `cursor` advances on rows and on heartbeats, so an
// idle stream resumes from the heartbeat's seq rather than replaying the tail
// since the last row; and a row at or below the fold's seq is dropped by
// `applyEvent`, so an overlap costs nothing.
//
// **A heartbeat is a seq marker, not a row.** It arrives as `event: heartbeat`
// with `data: {"seq": N}` and `id: N`; it moves `cursor` and reaches the
// reducer never. An ephemeral row arrives with no `id`, so it never moves the
// cursor and a resume never asks for it back (`sse.py`).

import type { LogRow } from "../contracts/stream";
import { HEARTBEAT_EVENT, streamPath } from "../contracts/stream";

/** The slice of `EventSource` this client uses, so a test can hand in a fake. */
export interface EventSourceLike {
  readonly readyState: number;
  onerror: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent<string>) => void) | null;
  addEventListener(type: string, listener: (event: MessageEvent<string>) => void): void;
  close(): void;
}

export type EventSourceFactory = (url: string) => EventSourceLike;

export interface RunStreamHandlers {
  /** A durable or ephemeral row, in stream order. */
  onRow: (row: LogRow) => void;
  /** The stream's cursor moved: a row's seq or a heartbeat's. */
  onSeq?: (seq: number) => void;
  /** Connection opened or a heartbeat confirmed an idle connection. */
  onConnected?: () => void;
  /** The `EventSource` reported an error; it reconnects on its own unless closed. */
  onError?: (event: Event) => void;
}

export interface RunStream {
  /** The last `id` seen: what the browser sends as `Last-Event-ID` on reconnect. */
  readonly cursor: number;
  readonly source: EventSourceLike;
  close(): void;
}

export const CLOSED = 2;

/** The frames a URL carries, folded through the handlers until `close()`. */
export function openRunStream(
  runId: string,
  handlers: RunStreamHandlers,
  options: { EventSource?: EventSourceFactory; baseUrl?: string } = {},
): RunStream {
  const factory = options.EventSource ?? ((url) => new EventSource(url));
  const source = factory(`${options.baseUrl ?? ""}${streamPath(runId)}`);
  let cursor = 0;

  const advance = (lastEventId: string) => {
    if (lastEventId === "") return;
    const seq = Number(lastEventId);
    if (Number.isInteger(seq) && seq > cursor) {
      cursor = seq;
      handlers.onSeq?.(seq);
    }
  };

  source.onmessage = (event: MessageEvent<string>) => {
    const row = JSON.parse(event.data) as LogRow;
    handlers.onRow(row);
    advance(event.lastEventId);
  };
  source.addEventListener("open", () => handlers.onConnected?.());
  source.addEventListener(HEARTBEAT_EVENT, (event: MessageEvent<string>) => {
    handlers.onConnected?.();
    advance(event.lastEventId);
  });
  source.onerror = (event: Event) => {
    handlers.onError?.(event);
  };

  return {
    get cursor() {
      return cursor;
    },
    source,
    close: () => source.close(),
  };
}
